import { useState } from 'react';
import './DealerPasswordChange.scss';
import { useSearchParams } from 'react-router';
import { LoadingCircle, PageTitle, Breadcrumbs, DealerMaskMessage, Icon, AlertBox } from '@gplibraries/react/components';
import DealerPasswordChangeForm from '../../components/DealerPasswordChange/DealerPasswordChangeForm';
import {
  useTokenProvider,
  useConfig,
  useDealerInfo,
  useAuthorizer,
  useB2BDomains,
} from '@gplibraries/react/hooks';
import { requestUserPasswordResetToken, updateUserPassword, getUserInfo } from '../../api/dealerPasswordChange';
import { useQuery } from '@tanstack/react-query';

export default function DealerPasswordChange() {
  const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
  const { data: dealerInfo, loading: dealerInfoLoading } = useDealerInfo();
  const getToken = useTokenProvider({ scopes: [dealerAccessApiScope]});
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [params] = useSearchParams();
  const resetToken = params?.get('token');
  const { data: B2BDomains } = useB2BDomains();

  // middleware token query
  const { data: token, isLoading: tokenLoading } = useQuery({
    enabled: Boolean(getToken),
    queryKey: ['middlewareToken'],
    queryFn: () => getToken()
  });
  const user = useAuthorizer(token?.accessTokenClaims);

  // Only need userInfo for dealer employee users.
  const { isLoading: userInfoLoading, error: userInfoError, data: userInfo } = useQuery({
    enabled: Boolean(user) && (user?.isDealerEmployeeUser()),
    queryKey: ['user_info', user?.get('id')],
    queryFn: () => getToken()
      .then(token => getUserInfo(dealerAccessApiHost, token?.accessToken, user?.get('id')))
      .then(res => res.json()),
    select: (data) => ({
      primaryEmail: data.data?.identities.find(id => id.signInType === 'emailAddress')?.issuerAssignedId,
      ...data?.data
    })
  })

  if (userInfoError) {
    console.error('[getUserInfo TEST FAILED]', userInfoError);
  }
  

  // ===============================
  // SEND RESET EMAIL (no token)
  // ===============================
  async function handleSendResetEmail() {
    setError(null);
    setEmailSent(false);

    try {
      // If the current user is a CS user or dealer, we are sending a pw reset email for
      // the master dealer account.
      const username = (user?.isCsUser() || user?.isDealer()) ? dealerInfo?.dealer_number : user.get('gpmUsername');
      const email = (user?.isCsUser() || user?.isDealer())
        ? dealerInfo?.primary_contact?.email
        : userInfo?.primaryEmail;

      if (!username) {
        throw new Error('Missing user identifier for password reset');
      }

      if (!email) {
        throw new Error('Missing email address for password reset');
      }

        const emailDomain = email.split('@')[1]?.toLowerCase();
              const isB2BDomain = B2BDomains?.some(
          (domain) => domain.toLowerCase() === emailDomain
        );

        if (isB2BDomain) {
          setError(
            "Password changes are not available for this email address. Your Dealer Access account uses your organization's sign-in system, please update your password using your company’s process."
          );
          return;
        }
      await getToken().then(token => requestUserPasswordResetToken(dealerAccessApiHost, token?.accessToken, username, email))

      setEmailSent(true);
    } catch (err) {
      // Reset token email request FAILED
      console.error('[Password Reset FAIL] Reset token email request FAILED', err);
      setError('Unable to send password reset email.');
    }

  }

  // ===============================
  // UPDATE PASSWORD (token present)
  // ===============================
  async function handlePasswordChange({ newPassword }) {
    if (user?.isCsUser()) {
      console.warn('[Password Change BLOCKED] CSUser attempted password update');
      setError('You are not authorized to update passwords.');
      return;
    }

    setError(null);
    setStatus(null);

    if (!resetToken) {
      console.error('[Password Change] Missing reset token');
      setError('Password reset token is missing or invalid.');
      return;
    }
    try {
      const response = await getToken()
        .then(token => updateUserPassword(dealerAccessApiHost, token?.accessToken, user?.get('id'), newPassword, resetToken))
        .then(res => res.json())

      if (!response.ok) {
            // Prefer JSON error message when available, fallback to text
            const contentType = response.headers.get('content-type') || '';

            if (contentType.includes('json')) {
              let payload;
              try {
                payload = await response.json();
              } catch {
                // ignore parse failure and fall through
              }

              throw new Error(
                payload?.message
                  ? `Password update failed (${response.status}) ${payload.message}`
                  : `Password update failed (${response.status})`
              );
            }

            let body = '';
            try {
              body = await response.text();
            } catch {}

            throw new Error(
              body
                ? `Password update failed (${response.status}) ${body}`
                : `Password update failed (${response.status})`
            );
          }
          setStatus('success');

    } catch (err) {
      console.error('[Password Change] FAILED', {
        message: err?.message,
        error: err,
      });

      setError(
        'Unable to update password. Please try again or request a new reset link.'
      );
    }
  }
  const canUseResetToken =
    Boolean(resetToken) &&
    (user?.isDealer() || user?.isDealerEmployeeUser());

  const loading = (userInfoLoading || dealerInfoLoading || tokenLoading);

  // ===============================
  // RENDER
  // ===============================
  return (<>
      
    <Breadcrumbs/>
    <PageTitle>Change Password</PageTitle>

    <div className="flex-col full">
      <DealerMaskMessage/>
      {loading
        ? <LoadingCircle className={'large color'}/>
        :
      <div className="dealer-password-reset-wrapper">


        {status === 'success' && (
          <div className="success">
            Password updated successfully.
          </div>
        )}



        {!canUseResetToken && (<>
          <h4>
            Send Password Reset Email
          </h4>
          {(userInfo?.primaryEmail || dealerInfo?.primary_contact?.email) && (
            <div className="dealer-primary-contact-email flex-inline">
              <Icon glyph={'envelope'} />
              <span>{(user?.isCsUser() || user?.isDealer()) ? dealerInfo?.primary_contact?.email ?? '(email not found)' : userInfo?.primaryEmail ?? '(email not found)'}</span>
            </div>
          )}

          {emailSent && (
            <div className="success">
              A password rest link has been sent to {userInfo?.primaryEmail ?? dealerInfo?.primary_contact?.email}.
            </div>
          )}

          {error && (
            <AlertBox type="error">
              {error}
            </AlertBox>
          )}

          {(userInfo?.primaryEmail || dealerInfo?.primary_contact?.email)
            ? (<>
              <button onClick={handleSendResetEmail}>
                Send Password Reset Email
              </button>
              <div className="disclaimer-message">
                Note: If you do not have access to the email on file and are the authorized user, please contact Credit at <strong>(800)
                472-0166</strong> to update your contact information.
              </div>
            </>)
            : (
              <AlertBox type={'warning'}>
                <p>
                  A valid email address is required to change your password and no email address
                  associated with this dealer was found. Please contact Credit at <strong>(800)
                  472-0166</strong> to update your contact information.
                </p>
              </AlertBox>
            )
          }

        </>)}

        {canUseResetToken && <>
          {error && (
            <AlertBox type="error">
              {error}
            </AlertBox>
          )}
          <DealerPasswordChangeForm
            user={userInfo}
            dealer={dealerInfo}
            onSubmit={handlePasswordChange}
          />
        </>}


      </div>
      }
    </div>
  </>);
}


